<template>
  <v-dialog :value="true" persistent class="policy-dialog" :max-width="$vuetify.breakpoint.xsOnly ? 338 : 573">
    <v-card class="rounded-lg position-relative">
      <div class="policy-content">
        <div class="policy-header">
          <div class="policy-text-container">
            <!-- Title -->
            <div class="policy-title">
              {{ dialogContent.title }}
            </div>

            <!-- Subtitle -->
            <p class="policy-subtitle mb-0">
              {{ dialogContent.description }}
            </p>
          </div>
        </div>

        <!-- Checkbox -->
        <div>
          <v-checkbox v-model="acceptedPolicy" color="#1F5AFF" hide-details class="policy-checkbox">
            <template v-slot:label>
              <span class="policy-subtitle">
                Acepto
                <template v-if="dialogContent.showTyC && dialogContent.showPG">
                  los
                  <a
                    href="https://vaitty.com/terminos"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary font-weight-medium text-decoration-none link-clickable"
                    @click.stop
                    >Términos y condiciones</a
                  >
                  y
                  <a
                    href="https://vaitty.com/politica-de-garantia"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary font-weight-medium text-decoration-none link-clickable"
                    @click.stop
                    >Política de Garantía</a
                  >
                </template>
                <template v-else-if="dialogContent.showTyC">
                  los
                  <a
                    href="https://vaitty.com/terminos"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary font-weight-medium text-decoration-none link-clickable"
                    @click.stop
                    >Términos y condiciones</a
                  >
                </template>
                <template v-else-if="dialogContent.showPG">
                  la
                  <a
                    href="https://vaitty.com/politica-de-garantia"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary font-weight-medium text-decoration-none link-clickable"
                    @click.stop
                    >Política de Garantía</a
                  >
                </template>
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- Accept Button -->
        <div :class="!$vuetify.breakpoint.xsOnly ? 'button-container-desktop' : ''">
          <v-btn
            :disabled="!acceptedPolicy"
            :loading="loading"
            variant="flat"
            size="large"
            :block="$vuetify.breakpoint.xsOnly"
            class="text-capitalize accept-button"
            style="letter-spacing: 0"
            :class="{ 'btn-enabled': acceptedPolicy, 'btn-desktop': !$vuetify.breakpoint.xsOnly }"
            @click="handleAccept"
          >
            ACEPTAR Y CONTINUAR
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PolicyAcceptanceDialog',
  data() {
    return {
      acceptedPolicy: false,
      tycHash: null,
      pgHash: null,
      otherHashes: [],
      policyHeaders: {},
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['policyStatus']),

    dialogContent() {
      const defaultContent = {
        title: 'Términos y condiciones y Política de Garantía',
        description: 'Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones y Política de Garantía.',
        showTyC: true,
        showPG: true,
      };

      if (!this.policyStatus) return defaultContent;

      if (this.policyStatus.already_signed === false) {
        return defaultContent;
      }

      if (this.policyStatus.already_signed === true) {
        const hasTyC = this.policyStatus.has_signed_tyc;
        const hasPG  = this.policyStatus.has_signed_pg;

        if (hasTyC === false && hasPG === true) {
          return {
            title: 'Actualización de Términos y condiciones',
            description: 'Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones, que fueron actualizados recientemente.',
            showTyC: true,
            showPG: false,
          };
        }

        if (hasPG === false && hasTyC === true) {
          return {
            title: 'Actualización de Política de Garantía',
            description: 'Para continuar utilizando la plataforma, es necesario que aceptes nuestra Política de Garantía, que fue actualizada recientemente.',
            showTyC: false,
            showPG: true,
          };
        }

        if (this.policyStatus.all_signed === false) {
          return {
            title: 'Actualización de Términos y condiciones y Política de Garantía',
            description: 'Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones y Política de Garantía, que fueron actualizados recientemente.',
            showTyC: true,
            showPG: true,
          };
        }
      }

      return defaultContent;
    },

    documentHashes() {
      const hashes = [];
      if (this.dialogContent.showTyC && this.tycHash) hashes.push(this.tycHash);
      if (this.dialogContent.showPG  && this.pgHash)  hashes.push(this.pgHash);
      hashes.push(...this.otherHashes);
      return hashes;
    },
  },
  async mounted() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user || !user.email) {
        console.error('No user found in localStorage');
        return;
      }

      // Import the auth module
      const authModule = await import('@/store/auth.js');
      const policyVersions = authModule.default.policyVersions || authModule.policyVersions;
      
      const response = await policyVersions();
      
      this.policyHeaders = response.headers || {};

      const docs = response.data.documents;
      const tyc = docs.find(d => d.document_type_code === 'TYC');
      const pg  = docs.find(d => d.document_type_code === 'PG');
      this.tycHash = tyc?.file_hash || null;
      this.pgHash  = pg?.file_hash  || null;
      this.otherHashes = docs
        .filter(d => d.document_type_code !== 'TYC' && d.document_type_code !== 'PG')
        .map(d => d.file_hash).filter(Boolean);
      
    } catch (error) {
      console.error('Error fetching policy versions:', error);
    }
  },
  methods: {
    ...mapActions(['acceptUserPolicy']),

    async handleAccept() {
      if (!this.acceptedPolicy) return;

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user || !user.email) {
        console.error('No user found');
        return;
      }

      this.loading = true;
      try {
        await this.acceptUserPolicy({
          params: {
            email: user.email,
            document_hashes: this.documentHashes,
            origin: 'PRESTADOR'
          },
          headers: this.policyHeaders
        });
        
        this.$emit('close');
      } catch (error) {
        console.error('Error accepting policy:', error);
        alert('Hubo un error al aceptar los términos. Por favor, intente nuevamente.');
      } finally {
        this.loading = false;
      }
    },
    
  }
};
</script>

<style scoped>
.position-relative {
  position: relative;
}

.policy-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.policy-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.policy-text-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.close-btn-flex {
  flex-shrink: 0;
  margin-top: -4px;
}

.policy-dialog :deep(.v-overlay__content) {
  box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14),
    0px 9px 46px 8px rgba(0, 0, 0, 0.12);
  width: 328px !important;
  max-width: 328px !important;
}

.v-card-title {
  white-space: normal !important;
}

.policy-title {
  color: rgba(0, 0, 0, 0.87);
  font-family: Montserrat, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.policy-subtitle {
  color: #607D8B;
  font-family: Montserrat, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
}

.policy-checkbox :deep(.v-label) {
  opacity: 1 !important;
  color: #607D8B !important;
}

.policy-checkbox a,
.link-clickable {
  pointer-events: auto !important;
  position: relative;
  z-index: 1;
  cursor: pointer !important;
}

.policy-checkbox a:hover,
.link-clickable:hover {
  text-decoration: underline !important;
}

.button-container-desktop {
  display: flex;
  justify-content: flex-end;
  height: 44px;
}

.btn-desktop {
  width: 200px !important;
  height: 100% !important;
  min-width: 200px !important;
}

.btn-enabled {
  background-color: #1976d2 !important;
  color: white !important;
}

:deep(.v-btn--disabled) {
  opacity: 1 !important;
}

.text-primary {
  color: #1976d2 !important;
}
</style>